import { ColorScale, green, pink } from 'bold-ui/lib/styles/colors'
import { StatusVideochamadaEnum } from 'graphql/types.generated'

import { RemoteVideocallParticipant, VideocallStream } from './model-videochamada'

const getUserIconColors = (isOwner: boolean): [ColorScale, ColorScale] => (isOwner ? [pink, green] : [green, pink])

// TODO (@RNG): Implementar testes (#14543)
export function getVideocallStreams(
  selfId: ID,
  localStream: MediaStream,
  remoteParticipants: RemoteVideocallParticipant[],
  displayStream: MediaStream,
  isOwner: boolean,
  presentingStreamsIds: ReadonlySet<ID>
) {
  const [localIconColor, remoteIconColor] = getUserIconColors(isOwner)

  return [
    {
      id: selfId,
      label: 'Você',
      stream: localStream,
      remote: false,
      presenting: !!localStream && presentingStreamsIds.has(localStream.id),
      color: localIconColor,
    },
    displayStream && {
      id: `${selfId}|presentation`,
      label: 'Você está apresentando',
      stream: displayStream,
      remote: false,
      presenting: presentingStreamsIds.has(displayStream.id),
      color: localIconColor,
    },
    ...getRemoteVideocallStreams(remoteParticipants[0], remoteIconColor, presentingStreamsIds),
  ].filterNotFalsy()
}

function getRemoteVideocallStreams(
  remoteParticipant: RemoteVideocallParticipant,
  color: ColorScale,
  presentingStreamsIds: ReadonlySet<ID>
) {
  if (!remoteParticipant) return []

  const isPresenting = (stream: MediaStream) => stream && presentingStreamsIds.has(stream.id)

  const streams = [...remoteParticipant.streams.values()]
  const activeStreams = streams.filter(
    (stream) => isPresenting(stream) || !stream?.getTracks().every((track) => track.muted)
  )
  // Garante que será exibido ao menos um participante para indicar que o peer remoto está conectado
  if (activeStreams.every(isPresenting)) activeStreams.push(null)

  return activeStreams.map((stream) => {
    const presenting = isPresenting(stream)
    return {
      id: `${remoteParticipant.id}|${stream?.id}`,
      label: presenting ? `${remoteParticipant.nome} está apresentando` : remoteParticipant.nome,
      stream: stream,
      remote: true,
      presenting,
      color,
    }
  })
}

export const isVideochamadaIniciada = (status: StatusVideochamadaEnum) =>
  status === StatusVideochamadaEnum.ATIVA ||
  status === StatusVideochamadaEnum.ATIVA_SEM_DONO ||
  status === StatusVideochamadaEnum.LOTADA

export const getVideochamadaAlert = (
  nomeProfissional: string,
  videochamadaIniciada: boolean,
  videochamadaLotada: boolean,
  videochamadaSemDono: boolean
): {
  tooltip: string
  alert?: string
} =>
  !videochamadaIniciada
    ? {
        tooltip: `A chamada estará disponível quando ${nomeProfissional} entrar`,
        alert: 'A chamada ainda não começou',
      }
    : videochamadaLotada
    ? {
        tooltip: 'No momento, as videochamadas e-SUS APS podem ter apenas dois participantes simultâneos.',
        alert: 'Número de participantes excedido',
      }
    : videochamadaSemDono
    ? {
        tooltip: `A chamada estará disponível quando ${nomeProfissional} entrar`,
      }
    : null

const hasActiveVideoTrack = (stream?: MediaStream) =>
  stream?.getVideoTracks().some((track) => track.enabled && track.readyState === 'live') ?? false

export const getPipStream = (streams: VideocallStream[]) => {
  const remoteScreenShareStream = streams.find((stream) => stream.remote && stream.presenting)
  if (hasActiveVideoTrack(remoteScreenShareStream?.stream)) return remoteScreenShareStream

  const remoteCameraStream = streams.find((stream) => stream.remote && !stream.presenting)
  if (hasActiveVideoTrack(remoteCameraStream?.stream)) return remoteCameraStream

  const localScreenShareStream = streams.find((stream) => !stream.remote && stream.presenting)
  if (hasActiveVideoTrack(localScreenShareStream?.stream)) return localScreenShareStream

  const localCameraStream = streams.find((stream) => !stream.remote && !stream.presenting)
  if (hasActiveVideoTrack(localCameraStream?.stream)) return localCameraStream
}
